import { useEffect, useState } from 'react';
import { useRouterState, useNavigate } from '@tanstack/react-router';
import { findRedirect as findStaticRedirect } from '@/lib/redirects';

interface DbRedirect {
  from_path: string;
  to_path: string;
  is_prefix: boolean;
}

export function RedirectHandler() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [dbRedirects, setDbRedirects] = useState<DbRedirect[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { restSelect } = await import('@/lib/supabase-rest');
      if (cancelled) return;
      const data = await restSelect<DbRedirect>(
        'redirects',
        'select=from_path,to_path,is_prefix&is_active=eq.true',
      );
      if (cancelled) return;
      if (data) setDbRedirects(data);
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    let timer: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(() => { void load(); }, { timeout: 4000 });
    } else {
      timer = window.setTimeout(() => { void load(); }, 1500);
    }

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const path = pathname;
    const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');

    for (const rule of dbRedirects) {
      if (rule.is_prefix) {
        if (normalizedPath.startsWith(rule.from_path)) {
          const remainder = normalizedPath.slice(rule.from_path.length);
          navigate({ to: rule.to_path + remainder, replace: true });
          return;
        }
      } else {
        if (normalizedPath === rule.from_path || normalizedPath === rule.from_path.replace(/\/$/, '')) {
          navigate({ to: rule.to_path, replace: true });
          return;
        }
      }
    }

    const staticRedirect = findStaticRedirect(path);
    if (staticRedirect) navigate({ to: staticRedirect, replace: true });
  }, [pathname, navigate, dbRedirects]);

  return null;
}
